import type { WifiNetwork } from "@volley/shared/coach-models";

/**
 * Turn a trainer's raw scan into a list of SSIDs to offer, strongest first.
 *
 * A scan reports one entry per access point, so a single network normally
 * appears several times over — once per radio per band. Hidden networks report
 * an empty essid and are dropped, since there is nothing to pick.
 *
 * Names are compared exactly. Two networks differing only in spacing or case
 * are genuinely different networks, and collapsing them would hide the very
 * mistake this list exists to prevent.
 */
export function toSsidOptions(networks: WifiNetwork[]): string[] {
    const strongestByEssid = new Map<string, number>();

    networks.forEach(({ essid, signal_level: signal }) => {
        if (!essid) {
            return;
        }

        const best = strongestByEssid.get(essid);
        if (best === undefined || signal > best) {
            strongestByEssid.set(essid, signal);
        }
    });

    return [...strongestByEssid.entries()]
        .sort(([, a], [, b]) => b - a)
        .map(([essid]) => essid);
}

/**
 * How many of the scanned access points are hiding their name.
 *
 * This counts access points, not networks, and it is deliberately not derived
 * from the length of {@link toSsidOptions}: most of the gap between the two is
 * the same network reported once per radio per band, not hidden networks.
 */
export function countHiddenNetworks(networks: WifiNetwork[]): number {
    return networks.filter(({ essid }) => !essid).length;
}

export default toSsidOptions;
